import type { TextComposerFrameVariantContentJson } from "@/features/TextComposerV2/frame/frameOutput";
import type { SmartOfferCategoryHint } from "@/frames/schemas";
import type { ProjectData } from "@grapesjs/studio-sdk";

// Canonical frame-variant mediums. Mirrors connect-api's models.ValidMediums
// (internal/models/medium.go) — the backend is the source of truth; keep these
// in sync when a medium is added there.
export const CHANNEL_MEDIUMS = ["email", "rcs", "sms", "mms"] as const;

/**
 * Value for the `dragon_tile` list filter and the create-modal picker meaning
 * "creatives with no Dragon tile". The API matches frames with no inner tile.
 */
export const UNASSIGNED_DRAGON_TILE = "unassigned";

export type SiteId = string;
export type DragonTileId = string;
export type InnerTileId = string;
export type FrameId = string;
export type FrameVariantId = string;
export type ChannelMedium = (typeof CHANNEL_MEDIUMS)[number];
export type EditorType =
  | "grapesjs_email"
  | "grapesjs_plaintext_email"
  | "grapesjs_html"
  | "html"
  | "true_plaintext_email";
export type FrameApprovalStatus = "pending" | "approved";
export type ReturnTo =
  | "tile"
  | "connector-tile"
  | "broadcast-v2"
  | "creative-sphere"
  | "dragon-tiles"
  | "marketing-calendar";
export type OrderBy = "create_date" | "modify_date" | "name";
export type Order = "asc" | "desc";

export type ExitCondition =
  | "EXIT_CONDITION_EMAIL_CLICK"
  | "EXIT_CONDITION_EMAIL_OPEN"
  | "EXIT_CONDITION_PURCHASE";

export interface FrameTags {
  part?: number;
  email_type?: string;
  /** When true, the tile is treated as transactional (consent bypass). */
  transactional?: boolean;
  delay_minutes?: number;
  audience_ids?: string[];
  tags?: string[];
  is_active?: boolean;
  exit_condition?: ExitCondition;
  medium?: "email" | "sms";
  events?: Array<{
    name: string;
    active: number;
  }>;
  // Index signature lets FrameTags satisfy schema-inferred metadata on payloads.
  [key: string]: unknown;
}

/** Frame-grain lifetime metrics embedded on frame/frame-variant responses. */
export interface FrameMetrics {
  /** Revenue per 1,000 sends, in `currencyCode` major units (dollars, not cents). */
  rpm: number;
  currencyCode: string;
}

export interface Frame {
  id: FrameId;
  inner_tile: InnerTileId;
  name: string;
  /**
   * INVARIANT: `is_active` and `approval_status` must stay in sync —
   * `is_active === (approval_status === "approved")`.
   *
   * `is_active` is the backend-authoritative flag (it decides whether the
   * frame is actually used at send time). `approval_status` is a FE-only field
   * the backend ignores for "should I use this frame". Because both exist,
   * code that flips one without the other causes drift, so always write them
   * together (see `useToggleApproval` in `@/api/dragon-tiles`). Callers that
   * only set one of these are almost certainly a bug.
   */
  is_active: boolean;
  status: string; // e.g. "draft"
  approval_status: FrameApprovalStatus;
  slug: string;
  frame_variants: FrameVariant[];
  metadata?: FrameTags;
  create_date: string;
  modify_date: string;
  deletedAt: string | null;
  metrics?: FrameMetrics | null;
  blueprint?: { id: string; slug: string } | null;
}

export interface ReviewConfig {
  max_reviews: number;
  min_score: number;
  verified_buyer_only: boolean;
  sort_by: string;
}

/** One hand-picked product on a static grid, resolved by variant id at send time. */
export interface ProductGridPick {
  vendor_product_id: string;
  vendor_variant_id: string;
}

export interface ProductGridConfig {
  items: string; // e.g., "upsell_items", "cart_items", "dynamic" (BE name for the Discover model), "static"
  max_products: number; // rows × cols from the layout
  /** Static grids only: the picks the backend resolves. Absent on model grids. */
  static_products?: ProductGridPick[];
  reviews?: ReviewConfig;
  show_source_badge?: boolean;
}

/** Single offer entry in frame config (keyed by slug in config.offers) */
export interface OfferInEmailConfig {
  fallback_code: string;
  /** Present on `smart:<id>` keys: the backend picks the offer per recipient. */
  smart?: { category_hint?: SmartOfferCategoryHint };
}

/** Map of offer slug -> { fallback_code } in frame variant config */
export type OffersConfig = Record<string, OfferInEmailConfig>;

export interface FrameVariantConfig {
  subject_line?: string;
  preview_text?: string;
  product_grids?: Record<string, ProductGridConfig>; // "0", "1", etc.
  offers?: OffersConfig;
}

export interface FrameVariant {
  id: FrameVariantId;
  frame: FrameId;
  email: string;
  medium: ChannelMedium;
  editor_type: EditorType;
  content_text: string | null;
  content_url: string | null;
  content_json: ProjectData | TextComposerFrameVariantContentJson | null;
  /** Plain-text email body; when set without HTML the backend sends text/plain. */
  content_plaintext?: string | null;
  create_date: string; // ISO date string
  modify_date: string; // ISO date string
  deleted_date: string | null; // ISO date string
  config: FrameVariantConfig;
  metrics?: FrameMetrics | null;
}

export type FramePreviewManifest =
  | { state: "image"; card_url: string; full_url: string }
  | { state: "fallback" };

export interface FramePreviewVariant extends FrameVariant {
  manifest: FramePreviewManifest | null;
}

export interface FramePreview extends Frame {
  frame_variants: FramePreviewVariant[];
}

export function isFramePreviewVariant(
  variant: FrameVariant,
): variant is FramePreviewVariant {
  return "manifest" in variant;
}

export interface InnerTileConfig {
  cadenceInterval?: "month" | "week" | "day";
  cadenceStartAt?: number;
  /** @deprecated Backend is migrating this to InnerTile.audience_id (rejoiner2#645). Read via getAudienceId(). */
  audience_id?: string;
}

export type FrameEventTag = NonNullable<FrameTags["events"]>[number];

export interface InnerTileFrameRef {
  id: string;
  name: string;
  is_active?: boolean | null;
  events?: FrameEventTag[];
}

export interface InnerTile {
  id: InnerTileId;
  dragonTileId: DragonTileId;
  name: string;
  description: string;
  is_active: boolean;
  order: number;
  category: string;
  messages: string[];
  audience_id?: string;
  config: InnerTileConfig;
  frames: InnerTileFrameRef[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface DragonTileInclusions {
  listIds?: string[];
  segmentIds?: string[];
  productIds?: string[];
  categoryIds?: string[];
  brandIds?: string[];
}

export interface DragonTileExclusions {
  listIds?: string[];
  segmentIds?: string[];
  productIds?: string[];
  countryCodes?: string[];
}

export interface DragonTileConfig {
  exclusions?: DragonTileExclusions;
  inclusions?: DragonTileInclusions;
  minPriceDropPercent?: number;
  newProductWindowDays?: number;
  cadenceInterval?: "week" | "month";
  cadenceStartAt?: number;
  maxStockCount?: number;
  minStockCount?: number;
}

export const DRAGON_TILE_ATTENTION_REASONS = [
  "DRAGON_TILE_ATTENTION_REASON_UNSPECIFIED",
  "DRAGON_TILE_ATTENTION_REASON_ACTIVE_NO_INNER_TILES",
  "DRAGON_TILE_ATTENTION_REASON_ACTIVE_ALL_AUDIENCES_INACTIVE",
  "DRAGON_TILE_ATTENTION_REASON_ACTIVE_NO_ACTIVE_FRAMES",
  "DRAGON_TILE_ATTENTION_REASON_INACTIVE_HAS_SENDABLE_AUDIENCES",
] as const;

export type DragonTileAttentionReason =
  (typeof DRAGON_TILE_ATTENTION_REASONS)[number];

export interface DragonTileAttentionEntities {
  all: boolean;
  names: string[];
  total: number;
}

/**
 * Entity fields are populated conditionally per reason (unset messages arrive
 * as null under the gateway's EmitUnpopulated protojson config). For
 * ACTIVE_NO_ACTIVE_FRAMES, inactive_frames is scoped to frames under the
 * active audiences only, and total 0 means those audiences have no frames at
 * all. For INACTIVE_HAS_SENDABLE_AUDIENCES, active_audiences carries only the
 * sendable ones (active audiences holding an active frame).
 */
export interface DragonTileAttentionDetail {
  reason: DragonTileAttentionReason;
  active_audiences: DragonTileAttentionEntities | null;
  inactive_audiences: DragonTileAttentionEntities | null;
  active_frames: DragonTileAttentionEntities | null;
  inactive_frames: DragonTileAttentionEntities | null;
}

export interface DragonTile {
  id: DragonTileId;
  site_id: number;
  name: string;
  order: number;
  category: string;
  is_active: boolean;
  is_activatable: boolean; // Whether the activate toggle should be enabled
  is_clickable: boolean; // Whether the tile can be clicked
  analog_sequence: boolean; // Whether this tile is permanently in analog sequencing mode
  type?: "basic" | "connector" | "byoc";
  config: DragonTileConfig;
  create_date: string; // ISO date string
  modify_date: string; // ISO date string
  deleted_date: string | null; // ISO date string
  inner_tiles: InnerTile[];
  slug: string;
  needs_attention: DragonTileAttentionDetail[];
}

export type TileStatus = "Active" | "Off" | "Needs Attention" | "Running";

// API Response/Request Types

// GET /api/v2/:site_id/dragonTiles
export type GetDragonTilesResponse = DragonTile[];

// GET /api/v2/:site_id/dragonTiles/:dragon_tile_id
export type GetDragonTileResponse = DragonTile;

// GET /api/v2/:site_id/frames/ (paginated response)
export interface GetFramesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Frame[];
}

export interface GetFramePreviewsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FramePreview[];
}

// PATCH /api/v2/:site_id/dragonTiles/:dragon_tile_id
export type UpdateDragonTilePayload = {
  id: DragonTileId;
  name?: string;
  order?: number;
  category?: string;
  is_active?: boolean;
  analog_sequence?: boolean;
  config?: DragonTileConfig;
  inner_tiles?: InnerTile[];
};

export type UpdateDragonTileResponse = DragonTile;

// POST /api/v2/:site_id/dragonTiles/:dragon_tile_id/innerTiles/
export type CreateInnerTilePayload = {
  innerTile: {
    name: string;
    is_active: boolean;
    audience_id?: string;
    config: InnerTileConfig;
  };
};

export interface CreateInnerTileResponse {
  innerTile: InnerTile;
}

// DELETE /api/v2/:site_id/dragon-tiles/:dragon_tile_id/innerTiles/:inner_tile_id/
export type DeleteInnerTileParams = {
  siteId: SiteId;
  dragonTileId: DragonTileId;
  innerTileId: InnerTileId;
};

// GET /api/v2/:site_id/frame_variants/:variant_id/versions/
export interface GetFrameVariantVersionsResponse {
  versions: Version[];
}

export interface Version {
  object_id: string;
  revision_id: number;
  field_dict: FrameVariant;
  user: string;
}
